import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Loader } from '@wix/design-system';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useSentry } from '../../providers/ErrorsProvider';
import { useResponsiveComponents } from '../../hooks/useResponsiveComponents';
import { TFormActions, TSubmit } from '../../reducers/formSubmissionReducer';

interface SearchButtonProps {
  onSubmit(token: string): void;
  disabled: boolean;
  formSubmission: TSubmit;
  setFormField: React.Dispatch<TFormActions>;
}

export const SearchButton: React.FC<SearchButtonProps> = ({
  onSubmit,
  disabled: disableButton,
  formSubmission,
  setFormField,
}) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { t } = useTranslation();
  const { sentryClient } = useSentry();
  const [Responsive] = useResponsiveComponents();

  const handleReCaptchaVerify = useCallback(async () => {
    if (formSubmission === 'submitting') {
      return;
    }

    if (!executeRecaptcha) {
      sentryClient.captureException(
        new Error('Execute recaptcha not yet available'),
      );
      return;
    }

    setFormField({ type: 'formSubmission', value: 'submitting' });
    const token = await executeRecaptcha('search_button_click');
    onSubmit(token);
  }, [executeRecaptcha, onSubmit, sentryClient]);

  return (
    <Responsive.Tooltip
      content={t('charge-locator.charge-locator-form.search-disabled-tooltip')}
      disabled={!disableButton}
    >
      <Button
        size="large"
        dataHook="search-button"
        fullWidth
        skin="standard"
        disabled={disableButton}
        onClick={handleReCaptchaVerify}
        showTooltip
      >
        <Box minWidth="54px" align="center">
          {formSubmission === 'submitting' ? (
            <Loader dataHook="search-button__loader" size="tiny" />
          ) : (
            t('charge-locator.charge-locator-form.search-CTA')
          )}
        </Box>
      </Button>
    </Responsive.Tooltip>
  );
};
